import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { FaFilter, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

// Property Master's own copy of the Excel-style header (label + sort + filter-icon
// checkbox popup). Deliberately not shared with Customer Master's version so the
// two pages stay fully independent.
const ColumnFilterHeader = (props) => {
  const { t } = useTranslation();
  const { displayName, column, setSort, getValues, selected, onChange, onClear } = props;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [sort, setSortState] = useState(column?.getSort?.() || null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const popupRef = useRef(null);

  useEffect(() => {
    const listener = () => setSortState(column?.getSort?.() || null);
    column?.addEventListener?.('sortChanged', listener);
    return () => column?.removeEventListener?.('sortChanged', listener);
  }, [column]);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e) => {
      if (popupRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener('mousedown', handleOutside);
    return () => window.removeEventListener('mousedown', handleOutside);
  }, [open]);

  const toggleOpen = (e) => {
    e.stopPropagation();
    const rect = btnRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + window.scrollY + 4, left: Math.max(8, rect.right + window.scrollX - 220) });
    setSearch('');
    setOpen((o) => !o);
  };

  const handleSortClick = () => {
    const next = sort === 'asc' ? 'desc' : sort === 'desc' ? null : 'asc';
    setSort(next);
    setSortState(next);
  };

  const values = Array.from(new Set((getValues?.() || []).map((v) => (v === null || v === undefined || v === '' || v === '0' ? '--' : String(v))))).sort();
  const filteredValues = values.filter((v) => v.toLowerCase().includes(search.toLowerCase()));
  const activeSelection = selected && selected.length ? selected : values;
  const isFiltered = !!(selected && selected.length && selected.length !== values.length);

  const toggleValue = (v) => {
    const base = selected && selected.length ? selected : values;
    const next = base.includes(v) ? base.filter((x) => x !== v) : [...base, v];
    onChange(next);
  };

  const selectAll = () => onChange(values);
  const clearAll = () => onChange([]);

  return (
    <div className="property-col-header">
      <div className="property-col-header-label" onClick={handleSortClick} title={t('common.clickToSort')}>
        <span>{displayName}</span>
        {sort === 'asc' ? <FaSortUp /> : sort === 'desc' ? <FaSortDown /> : <FaSort className="property-col-sort-idle" />}
      </div>
      <button type="button" ref={btnRef} className={`property-col-filter-btn ${isFiltered ? 'active' : ''}`} onClick={toggleOpen} title={t('common.filter')}>
        <FaFilter />
      </button>
      {open &&
        ReactDOM.createPortal(
          <div ref={popupRef} className="property-col-filter-popup" style={{ top: pos.top, left: pos.left }}>
            <input
              className="form-control form-control-sm mb-2"
              placeholder={t('common.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
            <div className="property-col-filter-actions">
              <span onClick={selectAll}>{t('common.selectAll')}</span>
              <span onClick={clearAll}>{t('common.clear')}</span>
            </div>
            <div className="property-col-filter-list">
              {filteredValues.length === 0 && <div className="text-muted small p-1">{t('common.noValues')}</div>}
              {filteredValues.map((v) => (
                <label key={v} className="property-col-filter-item">
                  <input type="checkbox" checked={activeSelection.includes(v)} onChange={() => toggleValue(v)} />
                  <span>{v}</span>
                </label>
              ))}
            </div>
            <div className="property-col-filter-footer">
              <button
                type="button"
                className="mainbtnRed"
                onClick={() => {
                  onClear();
                  setOpen(false);
                }}
              >
                {t('common.clearFilter')}
              </button>
              <button type="button" className="mainbtn" onClick={() => setOpen(false)}>
                {t('common.apply')}
              </button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default ColumnFilterHeader;
